/**
 * Step two — the terms, read back, and the one door that changes them.
 *
 * `.docs/20-studio-portal-pages.md` §B asks for "tier + currency (with the 'set at
 * onboarding' sentence)". `clients_create` set both a moment ago, so this step is not
 * a second form for them: it reads the account back and states what is now fixed
 * about it, which is the point at which a mistyped tier is cheap to fix and the point
 * after which it is not.
 *
 * **The tier has a later door and the currency has none.** `clients_set_tier!` is an
 * audited staff action and it is offered here, folded, for the case this screen exists
 * for. There is no `clients_set_currency`: `PRICING.md` says a client is billed in one
 * currency fixed at onboarding, and the honest answer to "I chose the wrong one" is to
 * archive the account and open another before anything is priced.
 */

import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Tag } from '../../../components/portal/Status.js'
import { Mono, Num, Prose } from '../../../components/portal/Text.js'
import { ActionForm } from '../../../components/studio/ActionForm.js'
import { useFormat } from '../../../format/useFormat.js'
import { useAccount } from './account.js'

export type TermsStepProps = {
  readonly client: string
  readonly onNext: () => void
}

export function TermsStep({ client, onNext }: TermsStepProps) {
  const format = useFormat()
  const { account } = useAccount(client)

  if (account === null) return <Empty>Reading the account…</Empty>

  const row = account.client

  return (
    <Stack gap="loose">
      <Kv>
        <KvRow k="Company" v={row.name} />
        <KvRow k="Slug" v={<Mono>{row.slug}</Mono>} />
        <KvRow k="Market tier" v={<Tag>{format.number(row.market_tier)}</Tag>} />
        <KvRow k="Billing currency" v={<Mono>{row.billing_currency}</Mono>} />
        <KvRow k="Timezone" v={<Mono>{row.timezone}</Mono>} />
        <KvRow k="Seats" v={<Num value={row.seat_limit} />} />
      </Kv>

      <Prose>
        The tier is the multiplier every block, project and extra on this account is priced through,
        and the currency is what every one of them is invoiced in. Both were set when the account
        was opened. Change the tier now if it is wrong — once the retainer starts, its tier is a
        snapshot and a later change moves new work only.
      </Prose>

      <Fold summary="Change the market tier">
        <ActionForm
          action="clients_set_tier"
          // The tier is drawn as a list — `market_tier` is `{ type: 'number', enum:
          // [1, 2, 3] }` — and `ActionForm` turns the row's number into the string a
          // <select> holds and back again on the way out.
          defaults={{ market_tier: row.market_tier }}
          fixed={{ client }}
          submitLabel="Set the tier"
        >
          <Prose>
            Audited, and it re-prices nothing that already exists. A retainer snapshots its tier at
            signup; a work order snapshots its own when it is priced.
          </Prose>
        </ActionForm>
      </Fold>

      <ButtonRow>
        <Button onClick={onNext} variant="primary">
          These are right — carry on
        </Button>
      </ButtonRow>
    </Stack>
  )
}
